import { currentOutboxOwner } from "./offline-link";
import { DEPENDENCY_PLACEHOLDER, enqueueOperation } from "./offline-outbox";
import { outboxSender } from "./offline-sender";

/**
 * Il punto unico di passaggio dei file.
 *
 * Le fotografie non viaggiano su tRPC ma su indirizzi di caricamento loro. Perche valga la
 * stessa promessa delle altre scritture, ogni caricamento dell'app passa da qui e da nessun
 * altro posto: offline il file resta sul dispositivo con la sua richiesta, e parte da solo.
 */

export type UploadOutcome =
  | { inviato: true; body: unknown }
  | { inviato: false; operationId: string };

export type UploadThroughOutbox = {
  /**
   * Indirizzo di caricamento. Quando dipende da un'azione ancora in coda contiene il
   * segnaposto `{{id}}`, che la coda sostituisce appena il server risponde.
   */
  url: string;
  field: string;
  filename: string;
  blob: Blob;
  label: string;
  /** L'azione in coda che assegna l'indirizzo, e il campo del suo risultato da leggere. */
  dependsOn?: { operationId: string; field: string } | null;
  online?: () => boolean;
};

export async function uploadThroughOutbox(input: UploadThroughOutbox): Promise<UploadOutcome> {
  const owner = currentOutboxOwner();
  const online = input.online ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine));
  // Letto una volta sola: gli stessi byte servono all'invio diretto e alla coda.
  const attachment = {
    field: input.field,
    filename: input.filename,
    mimeType: input.blob.type || "application/octet-stream",
    bytes: await input.blob.arrayBuffer(),
  };

  async function queue() {
    const operation = await enqueueOperation({
      companyId: owner!.companyId,
      userId: owner!.userId,
      kind: "upload",
      target: input.url,
      attachment,
      dependsOn: input.dependsOn?.operationId ?? null,
      dependsOnField: input.dependsOn?.field ?? null,
      label: input.label,
    });
    return { inviato: false as const, operationId: operation.id };
  }

  // Senza sessione non c'e coda a cui appartenere: si prova e basta, e l'errore si vede.
  if (!owner) return { inviato: true, body: await outboxSender.sendUpload(input.url, attachment) };

  // L'indirizzo non esiste ancora: dipende da un'azione che il server non ha visto.
  if (input.dependsOn || input.url.includes(DEPENDENCY_PLACEHOLDER)) return queue();
  if (!online()) return queue();

  try {
    return { inviato: true, body: await outboxSender.sendUpload(input.url, attachment) };
  } catch (error) {
    // Rete caduta a meta caricamento: il file resta sul dispositivo, non si perde.
    if (error instanceof Error && error.name === "NetworkUnavailableError") return queue();
    throw error;
  }
}
